/**
 * AuthProvider — initializes auth + PostHog on app mount.
 * #682: Also flushes the write-through queue on startup and periodically.
 * #710: Signals auth-ready gate after initialization.
 * #730: Syncs bookmarks/follows from Supabase after auth.
 */
import { useEffect, type ReactNode } from "react";
import { useAuthStore } from "@/store/auth";
import { initPostHog } from "@/lib/posthog";
import { flushWriteQueue } from "@/lib/write-through-queue";
import { getSupabase, markAuthReady } from "@/lib/supabase";
import { createLogger } from '@/lib/logger';

const log = createLogger('AuthProvider');
const FLUSH_INTERVAL_MS = 30_000; // retry failed writes every 30s

/** #730: One-time cloud sync — merge Supabase data into local stores */
async function syncCloudData() {
  try {
    const userId = useAuthStore.getState().user?.id;
    if (!userId || userId === 'anonymous') return;
    const sb = getSupabase();

    // Sync bookmarks
    const { data: bookmarks } = await sb
      .from('bookmarks')
      .select('target_id')
      .eq('user_id', userId);
    if (bookmarks && bookmarks.length > 0) {
      const { useSocialStore } = await import('@/store/social');
      const store = useSocialStore.getState();
      for (const b of bookmarks) {
        if (!store.isBookmarked(userId, 'atom', b.target_id)) {
          store.toggleBookmark(userId, 'atom', b.target_id);
        }
      }
      log.info('Cloud sync: bookmarks merged', { count: bookmarks.length });
    }

    // Sync follows
    const { data: follows } = await sb
      .from('follows')
      .select('following_id')
      .eq('follower_id', userId);
    if (follows && follows.length > 0) {
      const { useFollowStore } = await import('@/store/follow');
      const store = useFollowStore.getState();
      for (const f of follows) {
        if (!store.isFollowing(userId, f.following_id)) {
          store.toggleFollow(userId, f.following_id);
        }
      }
      log.info('Cloud sync: follows merged', { count: follows.length });
    }
  } catch (e) {
    log.warn('Cloud sync failed (non-blocking)', { err: String(e) });
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initPostHog();
    initialize().finally(() => {
      // #710: Unblock all queries waiting for auth session recovery
      markAuthReady();
      // #730: Sync cloud data after auth is ready (non-blocking)
      syncCloudData();
    });
    // #682: flush queued writes on startup
    flushWriteQueue(() => getSupabase());
    // periodic flush
    const timer = setInterval(() => flushWriteQueue(() => getSupabase()), FLUSH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [initialize]);

  return <>{children}</>;
}